<template>
    <div class="dashboard" :class="{ 'is-dark': $vuetify.theme.dark }">
        <v-card outlined class="control-bar">
            <v-card-text class="control-bar-body">
                <div class="control-row">
                    <div class="left-col">
                        <v-select
                            v-model="selectedEventId"
                            :items="events"
                            item-text="name"
                            item-value="id"
                            label="Event"
                            hide-details
                            dense
                        />
                        <div v-if="headerData" class="event-meta">
                            {{ headerData.startDate | luxonShortDate }} – {{ headerData.endDate | luxonShortDate }}
                            <span v-if="headerData.location"> · {{ headerData.location }}</span>
                        </div>
                    </div>
                    <div v-if="headerData" class="center-col">
                        <template v-if="headerData.daysUntilEvent === 0">
                            <div class="countdown-num">Event is Today!</div>
                        </template>
                        <template v-else>
                            <div class="countdown-num">{{ Math.abs(headerData.daysUntilEvent) }}</div>
                            <div class="countdown-label">{{ headerData.daysUntilEvent > 0 ? 'days until event' : 'days since event' }}</div>
                        </template>
                        <div v-if="showNextEventLine" class="next-event">
                            Next event is <strong>{{ nextHeaderData.name }}</strong> and is in {{ nextHeaderData.daysUntilEvent }} days
                        </div>
                    </div>
                    <div class="right-col">
                        <v-btn-toggle v-model="mode" mandatory>
                            <v-btn value="mine" small>Mine</v-btn>
                            <v-btn value="shared" small :disabled="!canShared">Shared</v-btn>
                            <v-btn value="all" small :disabled="!canAll">All</v-btn>
                        </v-btn-toggle>
                        <v-btn icon class="ml-2" @click="refresh"><v-icon>mdi-refresh</v-icon></v-btn>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <dashboard-message-widget :refresh-key="refreshKey" />

        <div v-if="!selectedEventId" class="dashboard-empty">
            No events available.
        </div>
        <v-row v-else dense class="dashboard-grid">
            <v-col cols="12" md="8">
                <encounter-status-widget :event-id="selectedEventId" :mode="mode" :refresh-key="refreshKey" />
            </v-col>
            <v-col cols="12" md="4">
                <requests-rumours-widget :event-id="selectedEventId" :mode="mode" :refresh-key="refreshKey" />
            </v-col>
            <v-col cols="12" md="8">
                <monster-demand-widget :event-id="selectedEventId" :mode="mode" :refresh-key="refreshKey" />
            </v-col>
            <v-col cols="12" md="4">
                <monster-types-widget :event-id="selectedEventId" :mode="mode" :refresh-key="refreshKey" />
            </v-col>
            <v-col cols="12" md="6">
                <plots-active-widget :event-id="selectedEventId" :mode="mode" :refresh-key="refreshKey" />
            </v-col>
            <v-col cols="12" md="6">
                <recent-activity-widget :event-id="selectedEventId" :mode="mode" :refresh-key="refreshKey" />
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import api from '@/api'
import DashboardMessageWidget from './widgets/DashboardMessageWidget.vue'
import EncounterStatusWidget from './widgets/EncounterStatusWidget.vue'
import RequestsRumoursWidget from './widgets/RequestsRumoursWidget.vue'
import MonsterDemandWidget from './widgets/MonsterDemandWidget.vue'
import MonsterTypesWidget from './widgets/MonsterTypesWidget.vue'
import PlotsActiveWidget from './widgets/PlotsActiveWidget.vue'
import RecentActivityWidget from './widgets/RecentActivityWidget.vue'

const STORAGE_EVENT_ID = 'worldmanager.dashboard.eventId'
const STORAGE_MODE = 'worldmanager.dashboard.mode'
const EVENT_TTL_MS = 24 * 60 * 60 * 1000

const DASHBOARD_ENTITIES = ['plot', 'encounter', 'rumour', 'lammie']

function saveEventId(id) {
    try {
        localStorage.setItem(STORAGE_EVENT_ID, JSON.stringify({ id, savedAt: Date.now() }))
    } catch (_) {
        // localStorage unavailable (private mode, quota) — silent
    }
}

function loadEventId() {
    try {
        const raw = localStorage.getItem(STORAGE_EVENT_ID)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return null
        const { id, savedAt } = parsed
        if (typeof id !== 'number' || typeof savedAt !== 'number') return null
        if (Date.now() - savedAt > EVENT_TTL_MS) {
            localStorage.removeItem(STORAGE_EVENT_ID)
            return null
        }
        return id
    } catch (_) {
        return null
    }
}

export default {
    name: 'Dashboard',
    components: {
        DashboardMessageWidget,
        EncounterStatusWidget, RequestsRumoursWidget,
        MonsterDemandWidget, MonsterTypesWidget, PlotsActiveWidget, RecentActivityWidget,
    },
    data() {
        return {
            selectedEventId: null,
            mode: 'mine',
            refreshKey: 0,
            headerData: null,
            nextHeaderData: null,
            headerInFlightKey: null,
            nextHeaderInFlightKey: null,
        }
    },
    computed: {
        ...mapState(['user', 'events']),
        ...mapGetters(['nextEvent', 'isAdmin']),
        canShared() {
            if (this.isAdmin) return true
            return DASHBOARD_ENTITIES.some(name => {
                const perms = this.user && this.user[name]
                return perms && (perms.shared.length > 0 || perms.all.length > 0)
            })
        },
        canAll() {
            if (this.isAdmin) return true
            return DASHBOARD_ENTITIES.some(name => {
                const perms = this.user && this.user[name]
                return perms && perms.all.length > 0
            })
        },
        showNextEventLine() {
            return this.nextHeaderData
                && this.nextEvent
                && this.nextEvent !== this.selectedEventId
        },
    },
    watch: {
        selectedEventId: {
            handler(val) {
                if (val) saveEventId(val)
                this.fetchHeader()
                this.fetchNextHeader()
            },
            immediate: false,
        },
        mode(val) {
            if (val) localStorage.setItem(STORAGE_MODE, val)
        },
        canShared(val) {
            if (!val && this.mode === 'shared') this.mode = 'mine'
        },
        canAll(val) {
            if (!val && this.mode === 'all') this.mode = 'mine'
        },
        nextEvent: 'fetchNextHeader',
        refreshKey() {
            this.fetchHeader()
            this.fetchNextHeader()
        },
    },
    mounted() {
        const storedMode = localStorage.getItem(STORAGE_MODE)
        if (storedMode === 'shared' && this.canShared) this.mode = 'shared'
        else if (storedMode === 'all' && this.canAll) this.mode = 'all'
        else this.mode = 'mine'

        const storedEventId = loadEventId()
        if (storedEventId != null && this.events.find(e => e.id === storedEventId)) {
            this.selectedEventId = storedEventId
        } else if (this.nextEvent) {
            this.selectedEventId = this.nextEvent
        } else if (this.events.length > 0) {
            this.selectedEventId = this.events[0].id
        }
    },
    methods: {
        refresh() {
            this.refreshKey += 1
        },
        async fetchHeader() {
            if (!this.selectedEventId) { this.headerData = null; return }
            const key = String(this.selectedEventId)
            this.headerInFlightKey = key
            try {
                const data = await api.get(`dashboard/event/${this.selectedEventId}`)
                if (this.headerInFlightKey !== key) return
                this.headerData = data
            } catch (err) {
                if (this.headerInFlightKey !== key) return
                this.headerData = null
            }
        },
        async fetchNextHeader() {
            if (!this.nextEvent || this.nextEvent === this.selectedEventId) {
                this.nextHeaderData = null
                return
            }
            const key = String(this.nextEvent)
            this.nextHeaderInFlightKey = key
            try {
                const data = await api.get(`dashboard/event/${this.nextEvent}`)
                if (this.nextHeaderInFlightKey !== key) return
                this.nextHeaderData = data
            } catch (err) {
                if (this.nextHeaderInFlightKey !== key) return
                this.nextHeaderData = null
            }
        },
    },
}
</script>

<style scoped>
.dashboard { padding: 8px; }
.control-bar { margin-bottom: 12px; position: sticky; top: 0; z-index: 2; }
.control-bar-body { padding-top: 12px; padding-bottom: 12px; }
.control-row { display: flex; align-items: center; gap: 16px; }
.left-col { flex: 0 0 320px; }
.center-col { flex: 1; text-align: center; }
.right-col { display: flex; align-items: center; }
.event-meta { font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px; }
.countdown-num { font-size: 24px; font-weight: 700; color: #1976d2; line-height: 1.1; }
.countdown-label { font-size: 11px; color: rgba(0,0,0,0.6); }
.next-event { font-size: 12px; color: rgba(0,0,0,0.7); margin-top: 4px; }
.dashboard.is-dark .event-meta { color: rgba(255,255,255,0.7); }
.dashboard.is-dark .countdown-label { color: rgba(255,255,255,0.7); }
.dashboard.is-dark .next-event { color: rgba(255,255,255,0.85); }
.dashboard-empty { padding: 40px; text-align: center; color: rgba(0,0,0,0.54); }
.dashboard.is-dark .dashboard-empty { color: rgba(255,255,255,0.6); }
.dashboard-grid { margin-top: 0; }
</style>
